import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: var(--background);
  position: relative;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  background: var(--primary-dark);
  filter: blur(150px);
  opacity: 0.2;
  top: -400px;
  right: -200px;
  z-index: 0;
`;

const BackgroundGlow2 = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: var(--accent);
  filter: blur(120px);
  opacity: 0.15;
  bottom: -300px;
  left: -200px;
  z-index: 0;
`;

const MainContent = styled.div`
  padding: 2rem 4rem;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const GridLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 30px 30px;
  z-index: 0;
`;

const HeroSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 3rem;
  height: 70vh;
`;

const HeroContent = styled.div`
  max-width: 70%;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  line-height: 1.1;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -1px;
`;

const HeroDescription = styled(motion.p)`
  font-size: 1.4rem;
  line-height: 1.6;
  color: var(--text-secondary);
  margin-bottom: 2.5rem;
  max-width: 85%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const PrimaryButton = styled(motion.button)`
  padding: 0.8rem 2rem;
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const SecondaryButton = styled(motion.button)`
  padding: 0.8rem 2rem;
  background: transparent;
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(99, 102, 241, 0.1);
  }
`;

const HeroImage = styled(motion.div)`
  width: 500px;
  height: 500px;
  position: relative;
  z-index: 2;
`;

const CircleContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Circle = styled.div`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, ${props => props.opacity});
  position: absolute;
  animation: pulse 4s infinite;
  animation-delay: ${props => props.delay}s;

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: ${props => props.opacity};
    }
    50% {
      transform: scale(1.05);
      opacity: ${props => props.opacity * 1.2};
    }
    100% {
      transform: scale(1);
      opacity: ${props => props.opacity};
    }
  }
`;

const AICore = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 0 30px rgba(99, 102, 241, 0.4);
  
  &::before {
    content: '';
    position: absolute;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: var(--background);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: var(--gradient-primary);
  }
`;

const AILogo = styled.div`
  z-index: 3;
  font-size: 2.5rem;
  color: white;
  font-weight: 800;
`;

const ParticlePoint = styled(motion.div)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color || 'var(--primary)'};
  position: absolute;
  filter: blur(1px);
  box-shadow: 0 0 10px ${props => props.color || 'var(--primary)'};
  top: ${props => props.top}%;
  left: ${props => props.left}%;
`;

const FeatureSection = styled.section`
  margin-top: 4rem;
  padding: 3rem 0;
  z-index: 2;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 2.5rem;
  text-align: center;
  color: var(--text-primary);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
`;

const FeatureCard = styled(motion.div)`
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: ${props => props.glowColor || 'var(--primary)'};
    filter: blur(60px);
    opacity: 0.2;
    top: -20px;
    right: -20px;
  }
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.color || 'rgba(99, 102, 241, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text-secondary);
`;

const features = [
  {
    icon: '🤖',
    title: 'Intelligent Policy Recommendations',
    description: 'Leverage our agentic AI to receive tailored insurance policies based on your unique needs and risk profile.',
    color: 'rgba(99, 102, 241, 0.2)',
    glowColor: 'var(--primary)'
  },
  {
    icon: '📊',
    title: 'Risk Assessment & Prediction',
    description: 'Advanced analytics and machine learning algorithms to assess and predict potential risks with unprecedented accuracy.',
    color: 'rgba(16, 185, 129, 0.2)',
    glowColor: 'var(--secondary)'
  },
  {
    icon: '⚡',
    title: 'Automated Claims Processing',
    description: 'Streamline claims with our AI agents that work 24/7 to process and resolve claims in record time.',
    color: 'rgba(6, 182, 212, 0.2)',
    glowColor: 'var(--accent)'
  },
  {
    icon: '🛡️',
    title: 'Fraud Detection & Prevention',
    description: 'Our AI continuously monitors for unusual patterns and behaviors to prevent fraud before it happens.',
    color: 'rgba(245, 158, 11, 0.2)',
    glowColor: 'var(--warning)'
  },
  {
    icon: '🔍',
    title: 'Personalized Customer Experience',
    description: 'AI agents learn your preferences and provide a truly personalized insurance experience tailored to your needs.',
    color: 'rgba(99, 102, 241, 0.2)',
    glowColor: 'var(--primary)'
  },
  {
    icon: '🔮',
    title: 'Predictive Policy Adjustment',
    description: 'Our AI proactively suggests policy adjustments based on changing circumstances and emerging risks.',
    color: 'rgba(16, 185, 129, 0.2)',
    glowColor: 'var(--secondary)'
  }
];

const LandingPage = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Animation for floating particles would go here
  }, []);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: i * 0.1,
        duration: 0.5
      } 
    })
  };

  return (
    <Container>
      <BackgroundGlow />
      <BackgroundGlow2 />
      <GridLines />
      
      <MainContent>
        <HeroSection>
          <HeroContent>
            <HeroTitle className="robotic-font"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              The Future of Insurance is Intelligent
            </HeroTitle>
            <HeroDescription
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Harness the power of our Agentic AI to revolutionize how you manage risks, policies, and claims in real-time.
            </HeroDescription>
            
            <ButtonGroup>
              <PrimaryButton
                as={Link}
                // to="/dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started →
              </PrimaryButton>
              
              <SecondaryButton
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Learn More
              </SecondaryButton>
            </ButtonGroup>
          </HeroContent>
          
          <HeroImage
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <CircleContainer>
              <Circle size={400} opacity={0.1} delay={0} />
              <Circle size={320} opacity={0.15} delay={0.5} />
              <Circle size={240} opacity={0.2} delay={1} />
              <AICore>
                <AILogo>AI</AILogo>
              </AICore>
              
              {/* Floating particle points */}
              {Array.from({ length: 15 }).map((_, i) => (
                <ParticlePoint
                  key={i}
                  color={i % 3 === 0 ? 'var(--primary)' : i % 3 === 1 ? 'var(--secondary)' : 'var(--accent)'}
                  top={Math.random() * 100}
                  left={Math.random() * 100}
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: [0.2, 0.8, 0.2], 
                    x: [0, Math.random() * 40 - 20, 0],
                    y: [0, Math.random() * 40 - 20, 0]
                  }}
                  transition={{ 
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2
                  }}
                />
              ))}
            </CircleContainer>
          </HeroImage>
        </HeroSection>
        
        <FeatureSection>
          <SectionTitle>Transform Your Insurance Experience</SectionTitle>
          
          <FeatureGrid>
            {features.map((feature, i) => (
              <FeatureCard 
                key={i}
                glowColor={feature.glowColor}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={cardVariants}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <FeatureIcon color={feature.color}>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </FeatureSection>
      </MainContent>
    </Container>
  );
};

export default LandingPage;
